import { isNil } from "lodash-es";
import { booleanType, ensure, ensureArray, numberType, stringType, TType } from "types";

import {
  configIdType,
  configPageableMetaType,
  configSystemRecordType,
  databaseUserType,
  IDatabaseUser,
  IId,
  IPageable,
  IPartialPageable,
  ISystemRecord,
} from "@/core/api";
import { dateType, TDate } from "@/core/dates";
import { htmlType } from "@/core/html";
import { Attachment, attachmentType } from "@/data/attachments";
import { companyType, ICompany } from "@/data/companies";
import {
  consultantProfileType,
  contactPersonType,
  IConsultantProfile,
  IContactPerson,
} from "@/data/consultant-profiles";
import { costCenterType, ICostCenter } from "@/data/cost-centers";
import { customFieldType, ICustomField } from "@/data/custom-fields";
import { IJobRequest, IOrigin, jobRequestType, originType } from "@/data/job-requests";
import { ILanguageWithLevel, languageWithLevelType } from "@/data/languages";
import { ILocation, locationType } from "@/data/locations";
import { ISkillWithPriority, skillWithPriorityType } from "@/data/skills";
import { TaskType } from "@/data/tasks";
import { IWithRouteMatchProps, IWithRoutePropsProps } from "@/infrastructure/Route";

export enum WorkOrderStatus {
  CONFIRMATION_PENDING = "CONFIRMATION_PENDING",
  CONFIRMED = "CONFIRMED",
  REJECTED = "REJECTED",
  WITHDRAW = "WITHDRAW",
  CLOSED = "CLOSED",
  CLOSURE_CONFIRMED = "CLOSURE_CONFIRMED",
}

export const openWorkOrderStatuses = [
  {
    code: WorkOrderStatus.CONFIRMED,
  },
  {
    code: WorkOrderStatus.CONFIRMATION_PENDING,
  },
  {
    code: WorkOrderStatus.CLOSURE_CONFIRMED,
  },
];

export interface IWorkOrderTask extends IId {
  externalAttachments: [Attachment];
  type: TaskType;
}

export interface IReference extends IId {}

export interface IWorkOrder extends IId, ISystemRecord {
  actualEndDate?: TDate;
  agreementDescription: string;
  businessUnitName: string;
  canBeAccepted: boolean;
  canBeRejected: boolean;
  candidateDidNotShow?: boolean;
  company: ICompany;
  contactPerson: IContactPerson;
  createdBy?: IDatabaseUser;
  createdDate: TDate;
  customFields: ICustomField[];
  endDate: TDate;
  estimatedCost: number;
  hourlyRate: number;
  hoursPerWeek: number;
  jobRequest?: IReference;
  jobRequestApplication?: IReference;
  jobRequestDetails: IJobRequest;
  languages: ILanguageWithLevel[];
  legalEntityName: string;
  legalEntityVatNo: string;
  level: string;
  levelDescription: string;
  location?: ILocation;
  modifiedBy?: IDatabaseUser;
  modifiedDate: TDate;
  origin: IOrigin;
  poNumber?: string;
  qualificationCriteria: string;
  rateCurrency: string;
  remoteWorkPercentage: number;
  role: string;
  roleDescription: string;
  skills: ISkillWithPriority[];
  startDate: TDate;
  status: WorkOrderStatus;
  statusComment?: string;
  supplierContactPersonEmail: string;
  supplierContactPersonName: string;
  supplierContactPersonPhone: string;
  supplierLegalName: string;
  supplierVatNo: string;
  title: string;
  totalEstimatedCost: number;
  version: number;
  tasks: [IWorkOrderTask];
  costCenter: ICostCenter | null;
  otherSupplier: boolean;
  consultantProfile: IConsultantProfile;
}

export const configReferenceType: TType<IReference> = {
  ...configIdType,
};

export const taskTypeType: TType<TaskType> = "JR_DESIGNATED_CANDIDATE_CREATE";

export const referenceType = ensure(configReferenceType);

export const workOrderTaskType: TType<IWorkOrderTask> = {
  id: numberType(),
  signature: stringType(),
  externalAttachments: ensureArray(attachmentType),
  type: stringType(),
};

export const configWorkOrderType: TType<IWorkOrder> = {
  ...configIdType,
  ...configSystemRecordType,
  actualEndDate: dateType(),
  agreementDescription: htmlType(),
  businessUnitName: stringType(),
  canBeAccepted: booleanType(),
  canBeRejected: booleanType(),
  candidateDidNotShow: booleanType(),
  company: companyType,
  contactPerson: contactPersonType,
  createdBy: databaseUserType,
  createdDate: dateType(),
  customFields: ensureArray(customFieldType),
  endDate: dateType(),
  estimatedCost: numberType(),
  hourlyRate: numberType(),
  hoursPerWeek: numberType(),
  jobRequest: v => (isNil(v) ? null : referenceType(v)),
  jobRequestApplication: v => (isNil(v) ? null : referenceType(v)),
  jobRequestDetails: jobRequestType,
  languages: ensureArray(languageWithLevelType),
  legalEntityName: stringType(),
  legalEntityVatNo: stringType(),
  level: stringType(),
  levelDescription: stringType(),
  location: locationType,
  modifiedBy: databaseUserType,
  modifiedDate: dateType(),
  origin: originType,
  poNumber: stringType(),
  qualificationCriteria: htmlType(),
  rateCurrency: stringType(),
  remoteWorkPercentage: numberType(),
  role: stringType(),
  roleDescription: htmlType(),
  skills: ensureArray(skillWithPriorityType),
  startDate: dateType(),
  status: stringType(),
  statusComment: stringType(),
  supplierContactPersonEmail: stringType(),
  supplierContactPersonName: stringType(),
  supplierContactPersonPhone: stringType(),
  supplierLegalName: stringType(),
  supplierVatNo: stringType(),
  title: stringType(),
  totalEstimatedCost: numberType(),
  version: numberType(),
  tasks: ensureArray(ensure(workOrderTaskType)),
  costCenter: v => (isNil(v) ? null : costCenterType(v)),
  otherSupplier: booleanType(false),
  consultantProfile: consultantProfileType,
};

export const workOrderType = ensure(configWorkOrderType);

export interface IWorkOrderIdMatchParam {
  workOrderId: string;
}

export interface IWorkOrderDetailRouteProps {
  workOrderId: number;
}

export interface IWorkOrderDetailMatchProps extends IWithRouteMatchProps<IWorkOrderIdMatchParam> {}
export interface IWorkOrderDetailRouteProps extends IWithRoutePropsProps<IWorkOrderDetailRouteProps> {}

export interface IWorkOrderRevision extends IWorkOrder {}

export type ActivityType = "CREATED" | "TASK_CREATED" | "TASK_APPROVED" | "TASK_REJECTED" | "CLOSED";

export interface IWorkOrderLifecycle extends IId {
  activityType: ActivityType;
  createdBy: IDatabaseUser;
  createdDate: TDate;
  modifiedBy: IDatabaseUser;
  modifiedDate: TDate;
  arg1?: string;
}

export const configWorkOrderLifecycleType: TType<IWorkOrderLifecycle> = {
  ...configIdType,
  activityType: stringType(),
  createdBy: databaseUserType,
  createdDate: dateType(),
  modifiedBy: databaseUserType,
  modifiedDate: dateType(),
  arg1: stringType(),
};

export const workOrderLifecycleType = ensure(configWorkOrderLifecycleType);

export interface IPageableWorkOrderLifecycle extends IPageable<IWorkOrderLifecycle[]> {}

export const pageableWorkOrderLifecycleTypeConfig: TType<IPageableWorkOrderLifecycle> = {
  ...configPageableMetaType,
  content: ensureArray(workOrderLifecycleType),
};

export const pageableWorkOrderLifecycleType = ensure(pageableWorkOrderLifecycleTypeConfig);

export interface IPartialPageableWorkOrderLifecycle extends IPartialPageable<IWorkOrderLifecycle[]> {}
